import { HttpResponse, delay, http } from "msw";

import {
  EXISTING_USER_EMAIL,
  WIRE_REJECTED_USER_EMAIL,
  TECHPORTAL_ROLES,
  createApiToken,
  createUser,
  deleteApiToken,
  deleteUser,
  deleteUserCredential,
  editUser,
  listApiTokens,
  listCompanies,
  listCustomers,
  listUserCredentials,
  listUsers,
  type CreateApiTokenBody,
  type CreateUserBody,
  type EditUserBody,
  type UserSortKey,
} from "../data/team";

/**
 * msw handlers for the Team-access endpoints (Users + API tokens — FRO-400).
 * Registered once in the router registry (adr/0003): the legacy tech-ui `/team`
 * view and the migrated `/settings/users|api-tokens` tabs both execute in the
 * router document and share this single worker + the one in-memory fixture, so a
 * create in either implementation is read back by the same list re-fetch.
 * Wildcard prefixes match regardless of the `/public` base each app builds against.
 *
 * The two lists deliberately disagree on everything (research/03 §5): the users
 * endpoint pages by `offset`/`limit` and sorts by `sort_key`/`sort_dir`, the
 * tokens endpoint pages by a **0-based** `page`/`pageSize` and cannot sort at all.
 * Both asymmetries are reproduced here, not smoothed over — smoothing them in the
 * fixture would hide the mapping bug the specs exist to catch.
 *
 * Note the trailing-slash asymmetry: the list GETs carry one, the creates do not.
 * msw's path matching treats a trailing slash as optional, so a single
 * wildcard-prefixed path without one covers both spellings.
 */

const USER_SORT_KEYS = [
  "createdAt",
  "operator",
  "company",
  "fullName",
  "email",
] as const;

function parseSortKey(value: string | null): UserSortKey {
  return (USER_SORT_KEYS as readonly string[]).includes(value ?? "")
    ? (value as UserSortKey)
    : "createdAt";
}

function parseInteger(value: string | null, fallback: number): number {
  const parsed = Number.parseInt(value ?? "", 10);
  return Number.isNaN(parsed) ? fallback : parsed;
}

/** Label that makes the token create endpoint answer slowly (in-flight spec). */
const SLOW_CREATE_SENTINEL_LABEL = "e2e-slow-create-token";

/** One entry of the backend's `details[]`: the rejected field, its rule, its message. */
interface JoiDetail {
  key: string;
  type: string;
  message: string;
}

/**
 * The tech-api's validation failure, verbatim (research/03 §5.11): the reason lives
 * in `details[]`, and the top-level message names nothing on its own. Reproduced
 * rather than flattened to a readable string — flattening it here would hide whether
 * the client can read the real shape.
 */
function joiValidationError(details: JoiDetail[]) {
  return {
    error: {
      message: "Joi Validation error",
      statusCode: 400,
      code: "BadInput",
      details,
    },
  };
}

/**
 * The user-write flags the backend takes as the NUMBERS `0`/`1`. Validated here so
 * the fixture rejects exactly what production rejects: a tolerant mock accepting a
 * boolean `sendEmail` is what let FRO-477 reach production green.
 */
const NUMERIC_USER_FLAGS = ["sendEmail", "betaTester"] as const;

/**
 * Joi's own reporting, reproduced: a non-`0`/`1` NUMBER trips the whitelist alone,
 * while a value of the wrong type trips the type rule as well — which is why the
 * captured FRO-477 body carried two details for its one boolean field.
 */
function numericFlagDetails(body: Record<string, unknown>): JoiDetail[] {
  return NUMERIC_USER_FLAGS.flatMap((key) => {
    const value = body[key];
    if (value === undefined || value === 0 || value === 1) return [];

    const whitelist: JoiDetail = {
      key,
      type: "any.only",
      message: `"${key}" must be one of [0, 1]`,
    };

    return typeof value === "number"
      ? [whitelist]
      : [
          whitelist,
          { key, type: "number.base", message: `"${key}" must be a number` },
        ];
  });
}

/**
 * What the FRO-477 report captured: the details the endpoint answers for the boolean
 * `sendEmail` the migrated dialog used to send. Derived from the validator rather than
 * restated, so the sentinel cannot drift from the contract it exists to demonstrate.
 */
const SENTINEL_WIRE_DETAILS: JoiDetail[] = numericFlagDetails({
  sendEmail: true,
});

const teamHandlers = [
  // Users list. `offset`/`limit` page it; `sort_key`/`sort_dir` sort it;
  // `search` matches usernames as well as name/email; `roleId`/`operatorId`/
  // `companyId` combine as AND.
  http.get("*/admin/users", ({ request }) => {
    const url = new URL(request.url);
    return HttpResponse.json(
      listUsers({
        offset: parseInteger(url.searchParams.get("offset"), 0),
        limit: parseInteger(url.searchParams.get("limit"), 24),
        sortKey: parseSortKey(url.searchParams.get("sort_key")),
        sortDir: url.searchParams.get("sort_dir") === "asc" ? "asc" : "desc",
        search: url.searchParams.get("search") ?? undefined,
        roleId: url.searchParams.get("roleId") ?? undefined,
        operatorId: url.searchParams.get("operatorId") ?? undefined,
        companyId: url.searchParams.get("companyId") ?? undefined,
      })
    );
  }),

  // Create. Answers 201 with `success` as the STRING "true". A seeded address
  // drives the duplicate-email path; its 400 envelope is the app's real error
  // shape (the duplicate case itself was never captured live — research/03 §8.9,
  // verified at the live dev-data gate).
  http.post("*/admin/users", async ({ request }) => {
    const raw = (await request.json()) as Record<string, unknown>;

    const flagDetails = numericFlagDetails(raw);
    if (flagDetails.length > 0) {
      return HttpResponse.json(joiValidationError(flagDetails), {
        status: 400,
      });
    }

    const body = raw as unknown as CreateUserBody;
    if (body.email === WIRE_REJECTED_USER_EMAIL) {
      return HttpResponse.json(joiValidationError(SENTINEL_WIRE_DETAILS), {
        status: 400,
      });
    }
    if (body.email === EXISTING_USER_EMAIL) {
      return HttpResponse.json(
        {
          error: {
            message: "Email is already in use",
            statusCode: 400,
            code: "BadInput",
          },
        },
        { status: 400 }
      );
    }
    return HttpResponse.json(createUser(body), { status: 201 });
  }),

  // Reset MFA. Registered before the plain user DELETE so the longer path wins.
  // The wire shape was authored from `tech-ui src/api/admin.js:128-135` and is
  // confirmed at the live dev-data gate (design D2).
  http.delete(
    "*/admin/users/:userId/credentials/:credentialId",
    ({ params }) => {
      deleteUserCredential(String(params.userId), String(params.credentialId));
      return HttpResponse.json({ success: true });
    }
  ),

  http.get("*/admin/users/:userId/credentials", ({ params }) =>
    HttpResponse.json(listUserCredentials(String(params.userId)))
  ),

  // Edit — the body carries only the changed text fields plus the four
  // always-sent ones. Answers `success` as a BOOLEAN, unlike create.
  http.put("*/admin/users/:userId", async ({ request, params }) => {
    const raw = (await request.json()) as Record<string, unknown>;

    const flagDetails = numericFlagDetails(raw);
    if (flagDetails.length > 0) {
      return HttpResponse.json(joiValidationError(flagDetails), {
        status: 400,
      });
    }

    editUser(String(params.userId), raw as unknown as EditUserBody);
    return HttpResponse.json({ success: true });
  }),

  http.delete("*/admin/users/:userId", ({ params }) => {
    deleteUser(String(params.userId));
    return HttpResponse.json({ success: true });
  }),

  // A BARE array — no `data` wrapper (research/03 §5.7).
  http.get("*/admin/techportal-roles", () =>
    HttpResponse.json(TECHPORTAL_ROLES)
  ),

  // Customers (companies v2). Registered before the `*/companies/` list and
  // before tech-ui's `*/companies/v2/:companyId` shell lookup, neither of which
  // matches this path: the by-id handler needs a non-empty id segment, which is
  // why the list request went unanswered and left the legacy Customer select on
  // "Loading..." until this handler existed.
  http.get("*/companies/v2/", ({ request }) => {
    const url = new URL(request.url);
    const cpoId = url.searchParams.get("cpoId");
    return HttpResponse.json(
      listCustomers({
        offset: parseInteger(url.searchParams.get("offset"), 0),
        limit: parseInteger(url.searchParams.get("limit"), 24),
        cpoId: cpoId === null ? undefined : parseInteger(cpoId, 0),
      })
    );
  }),

  // Companies. `operatorId` drives the Operator -> Customer cascade; the
  // synthetic id-0 row is prepended outside the page window.
  http.get("*/companies/", ({ request }) => {
    const url = new URL(request.url);
    const operatorId = url.searchParams.get("operatorId");
    return HttpResponse.json(
      listCompanies({
        offset: parseInteger(url.searchParams.get("offset"), 0),
        limit: parseInteger(url.searchParams.get("limit"), 24),
        operatorId:
          operatorId === null ? undefined : parseInteger(operatorId, 0),
      })
    );
  }),

  // Tokens list. `page`/`pageSize` page it (0-based `page`, unlike the users
  // list's offset); `search` narrows it over the row's visible text — an ASSUMED
  // field set, because the live endpoint has never answered a `search` param
  // successfully for us to read the real one off (see the live checklist).
  http.get("*/admin/integration-api-keys", ({ request }) => {
    const url = new URL(request.url);
    return HttpResponse.json(
      listApiTokens({
        page: parseInteger(url.searchParams.get("page"), 0),
        pageSize: parseInteger(url.searchParams.get("pageSize"), 24),
        search: url.searchParams.get("search") ?? undefined,
      })
    );
  }),

  // Create — a FLAT 201 response carrying the secret under `key`, the only place
  // it ever appears.
  http.post("*/admin/integration-api-keys", async ({ request }) => {
    const body = (await request.json()) as CreateApiTokenBody;
    // The secret is revealed exactly once, so the dialog must refuse every exit
    // while the request is in flight. That window is unobservable against an
    // instant mock, and Playwright's network layer cannot help because this suite
    // answers app-side (ADR 0003) — so the spec asks for a slow response by label,
    // the same sentinel idiom the settings handlers use for their failure paths.
    if (body.label === SLOW_CREATE_SENTINEL_LABEL) {
      await delay(2000);
    }
    return HttpResponse.json(createApiToken(body), { status: 201 });
  }),

  // Delete — answers **201**, not 200/204.
  http.delete("*/admin/integration-api-keys/:tokenId", ({ params }) => {
    deleteApiToken(Number(params.tokenId));
    return HttpResponse.json({ success: true }, { status: 201 });
  }),
];

export { teamHandlers, SLOW_CREATE_SENTINEL_LABEL };
